import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';

export type SessionArchiveStatus =
  | 'Execution Asset Produced'
  | 'Governance Caution'
  | 'Fiduciary Review Required'
  | string;

export type SessionArchiveRow = {
  id: string;
  title: string | null;
  framework: string | null;
  compassScore: number | null;
  lifecycleStatus: string;
  statusLabel: SessionArchiveStatus;
  lastGovernanceEvent: string | null;
  updatedAt: string;
  pendingReview: boolean;
};

export type SessionArchiveFilters = {
  page: number;
  pageSize: number;
  frameworkFilter: string;
  attentionOnly: boolean;
};

export type SessionArchivePage = {
  rows: SessionArchiveRow[];
  totalCount: number;
};

type DecisionRow = {
  id: string;
  issue_title: string | null;
  status: string;
  primary_framework: string | null;
  governance_score: number | null;
  created_at: string;
};

type LedgerRow = {
  decision_id: string;
  step: string;
  created_at: string;
};

type ApprovalStateRow = {
  decision_id: string;
  status: string;
};

const PENDING_APPROVAL_STATUSES = new Set(['pending', 'under_review']);

const CAUTION_THRESHOLD = 0.8;

function toArchiveStatus(lifecycleStatus: string, governanceScore: number | null): SessionArchiveStatus {
  if (lifecycleStatus === 'escalated') return 'Fiduciary Review Required';
  if (lifecycleStatus === 'execution_asset_produced') return 'Execution Asset Produced';
  if (governanceScore !== null && governanceScore < CAUTION_THRESHOLD) return 'Governance Caution';
  return lifecycleStatus;
}

function mapStepToEvent(step: string): string {
  if (step === 'governance_check') return 'Governance review completed';
  if (step === 'escalation') return 'Marked for fiduciary review';
  if (step === 'execution_asset') return 'Execution asset produced';
  return step.replaceAll('_', ' ');
}

function applyArchiveFilters<T extends { eq: (col: string, val: string) => T; or: (filters: string) => T }>(
  query: T,
  filters: Pick<SessionArchiveFilters, 'frameworkFilter' | 'attentionOnly'>,
): T {
  let next = query;

  if (filters.frameworkFilter !== 'all') {
    next = next.eq('primary_framework', filters.frameworkFilter);
  }

  if (filters.attentionOnly) {
    next = next.or(
      `status.eq.escalated,and(governance_score.lt.${CAUTION_THRESHOLD},status.neq.execution_asset_produced,status.neq.escalated)`,
    );
  }

  return next;
}

async function fetchSessionArchive(
  orgId: string,
  filters: SessionArchiveFilters,
): Promise<SessionArchivePage> {
  const from = (filters.page - 1) * filters.pageSize;
  const to = from + filters.pageSize - 1;

  let query = supabaseClient
    .from('decisions')
    .select('id, issue_title, status, primary_framework, governance_score, created_at', { count: 'exact' })
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });

  query = applyArchiveFilters(query, filters);

  const { data: decisions, error, count } = await query.range(from, to);

  if (error) throw new Error(error.message);

  const rows = (decisions ?? []) as DecisionRow[];
  const totalCount = count ?? 0;

  if (rows.length === 0) {
    return { rows: [], totalCount };
  }

  const ids = rows.map((r) => r.id);

  const [{ data: ledgerRows }, { data: approvalRows }] = await Promise.all([
    supabaseClient
      .from('ledger_entries')
      .select('decision_id, step, created_at')
      .in('decision_id', ids)
      .in('step', ['governance_check', 'escalation', 'execution_asset'])
      .order('created_at', { ascending: false }),
    supabaseClient
      .from('approval_states')
      .select('decision_id, status')
      .in('decision_id', ids),
  ]);

  const latestEventByDecision = new Map<string, LedgerRow>();
  for (const row of (ledgerRows ?? []) as LedgerRow[]) {
    if (!latestEventByDecision.has(row.decision_id)) {
      latestEventByDecision.set(row.decision_id, row);
    }
  }

  const pendingReviewByDecision = new Map<string, boolean>();
  for (const row of (approvalRows ?? []) as ApprovalStateRow[]) {
    if (PENDING_APPROVAL_STATUSES.has(row.status)) {
      pendingReviewByDecision.set(row.decision_id, true);
    }
  }

  const mapped = rows.map((row) => {
    const latest = latestEventByDecision.get(row.id);
    const updatedAt = latest?.created_at ?? row.created_at;
    const pendingReview = pendingReviewByDecision.get(row.id) ?? false;

    return {
      id: row.id,
      title: row.issue_title,
      framework: row.primary_framework,
      compassScore: row.governance_score,
      lifecycleStatus: row.status,
      statusLabel: toArchiveStatus(row.status, row.governance_score),
      lastGovernanceEvent: latest ? mapStepToEvent(latest.step) : null,
      updatedAt,
      pendingReview,
    };
  });

  return { rows: mapped, totalCount };
}

async function fetchArchiveFrameworks(orgId: string): Promise<string[]> {
  const { data, error } = await supabaseClient
    .from('decisions')
    .select('primary_framework')
    .eq('org_id', orgId)
    .not('primary_framework', 'is', null);

  if (error) throw new Error(error.message);

  const unique = new Set<string>();
  for (const row of data ?? []) {
    const fw = (row as { primary_framework: string | null }).primary_framework;
    if (fw) unique.add(fw);
  }

  return Array.from(unique).sort((a, b) => a.localeCompare(b));
}

const useSessionArchive = (filters: SessionArchiveFilters) => {
  const { userProfile } = useAuthContext();
  const orgId = userProfile?.org_id;

  return useQuery({
    queryKey: [
      'session-archive',
      orgId,
      filters.page,
      filters.pageSize,
      filters.frameworkFilter,
      filters.attentionOnly,
    ],
    queryFn: () => fetchSessionArchive(orgId!, filters),
    enabled: !!orgId,
  });
};

export const useArchiveFrameworks = () => {
  const { userProfile } = useAuthContext();
  const orgId = userProfile?.org_id;

  return useQuery({
    queryKey: ['session-archive-frameworks', orgId],
    queryFn: () => fetchArchiveFrameworks(orgId!),
    enabled: !!orgId,
    staleTime: 60_000,
  });
};

export default useSessionArchive;
